import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { DataProvider, useDataContext } from "./Components/DataContext";
import ErrorBoundary from "./Components/ErrorBoundary/ErrorBoundary";
import Layout from "./Components/Layout/Layout";
import AddBankDetails from "./Pages/Signup/Addbank";
import Signupvendor from "./Pages/Signup/Signupvendor";
import Finishsignup from "./Pages/Signup/Finishsignup";
import ForgotPassword from "./Pages/Login/ForgotPassword";
import OTPVerification from "./Pages/Login/VerifyOtp";
import ResetPassword from "./Pages/Login/ResetPassword";
import AddService from "./Pages/ServiceManagment/addService";
import ViewService from "./Pages/ServiceManagment/ViewService";
import ViewOrder from "./Pages/MerchantManagement/ViewOrder";
import Toastify from "./utils/tostfy";
import { resolveVendorServiceType } from "./utils/vendorServiceAccess";

const NotificationPage = React.lazy(() =>
  import("./Pages/Notification/Notification")
);
const CMSManagement = React.lazy(() =>
  import("./Pages/CMS Management/CMSManagement")
);
const Privacy = React.lazy(() =>
  import("./Pages/CMS Management/Privacy_policy")
);
const FAQ = React.lazy(() => import("./Pages/CMS Management/FAQ"));
const Login = React.lazy(() => import("./Pages/Login/Login"));
const SignUp = React.lazy(() => import("./Pages/Signup/Signup"));
const UpdatePassword = React.lazy(() => import("./Pages/Login/UpdatePassword"));
const Dashboard = React.lazy(() => import("./Pages/Dashboard/Dashboard"));
const UserManagement = React.lazy(() =>
  import("./Pages/UserManagement/UserManagement")
);
const ServiceManagement = React.lazy(() =>
  import("./Pages/ServiceManagment/serviceManagment")
);
const RejectedServices = React.lazy(() =>
  import("./Pages/ServiceManagment/rejectedServices")
);
const MediaManagement = React.lazy(() =>
  import("./Pages/ServiceManagment/MediaManagement")
);
const BookingManagement = React.lazy(() =>
  import("./Pages/BookingManagement/BookingManagement")
);
const CateringBookingManagement = React.lazy(() =>
  import("./Pages/BookingManagement/CateringBookingManagement")
);
const BookingDetails = React.lazy(() =>
  import("./Pages/BookingManagement/BookingDetails")
);
const SupportTickets = React.lazy(() =>
  import("./Pages/Support/SupportTickets")
);
const SupportTicketDetail = React.lazy(() =>
  import("./Pages/Support/SupportTicketDetail")
);
const PackageManagementLayout = React.lazy(() =>
  import("./Pages/PackageManagement/PackageManagementLayout")
);
const PackageInquiryList = React.lazy(() =>
  import("./Pages/PackageManagement/PackageInquiryList")
);
const PackageBookingList = React.lazy(() =>
  import("./Pages/PackageManagement/PackageBookingList")
);
const PackageInquiryDetail = React.lazy(() =>
  import("./Pages/PackageManagement/PackageInquiryDetail")
);
const PackageBookingDetail = React.lazy(() =>
  import("./Pages/PackageManagement/PackageBookingDetail")
);
const VendorDisputeList = React.lazy(() =>
  import("./Pages/Disputes/VendorDisputeList")
);
const VendorDisputeDetail = React.lazy(() =>
  import("./Pages/Disputes/VendorDisputeDetail")
);
const MyProfile = React.lazy(() => import("./Pages/Profile/Profile"));
const Setting = React.lazy(() => import("./Pages/Profile/ChangePassword"));
const ProfileUnderReview = React.lazy(() =>
  import("./Pages/ProfileUnderReview/ProfileUnderReview")
);
const ProductManagement = React.lazy(() =>
  import("./Pages/ProjectManagement/ProductManagement")
);
const MerchantManagement = React.lazy(() =>
  import("./Pages/MerchantManagement/MerchantManagement")
);
const AddProductManagement = React.lazy(() =>
  import("./Pages/ProjectManagement/AddProductManagement")
);
const ViewProductManagement = React.lazy(() =>
  import("./Pages/ProjectManagement/ViewProductManagement")
);
const Offers = React.lazy(() =>
  import("./Pages/ProjectManagement/Offers")
);
const Inventory = React.lazy(() =>
  import("./Pages/ProjectManagement/Inventory")
);

function ProductManagementIndexRedirect() {
  const { vendorProfile } = useDataContext();
  const t = resolveVendorServiceType(vendorProfile);
  if (t === "rent") return <Navigate to="rent" replace />;
  if (t === "service") return <Navigate to="/service-management" replace />;
  return <Navigate to="shop" replace />;
}

function MerchantManagementIndexRedirect() {
  const { vendorProfile } = useDataContext();
  const t = resolveVendorServiceType(vendorProfile);
  if (t === "rent") return <Navigate to="rent" replace />;
  if (t === "service") return <Navigate to="/service-management" replace />;
  return <Navigate to="shop" replace />;
}

const AppLoadingFallback = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#f5f5f5",
    }}
  >
    <div className="text-center">
      <div
        className="spinner-border text-success"
        role="status"
        style={{ width: "3rem", height: "3rem" }}
      >
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="mt-3 mb-0 text-muted">Loading...</p>
    </div>
  </div>
);

function App() {
  // const isSessionExpired = () => {
  //   const lastActivityTime = localStorage.getItem("lastActivityTime");
  //   if (lastActivityTime) {
  //     return Date.now() - parseInt(lastActivityTime, 10) > 60 * 60 * 1000; // 1 hour in milliseconds
  //   }
  //   return true;
  // };
  // useEffect(() => {
  //   if (isSessionExpired()) {
  //     localStorage.clear();
  //   }
  // }, []);
  const PrivateOutlet = () => {
    const loginSuccess = useSelector(
      (state) => state.loginInfo.userInformation
    );
    const storedStatus = localStorage.getItem("status");
    let isLoggedIn = false;
    try {
      isLoggedIn =
        loginSuccess?.code === 200 ||
        (storedStatus && JSON.parse(storedStatus) === 200);
    } catch (error) {
      console.error("Error parsing localStorage data:", error);
    }
    return isLoggedIn ? <Outlet /> : <Navigate to="/" replace />;
  };
  return (
    <DataProvider>
      <Router>
        <ErrorBoundary>
          {/* One ToastContainer for the whole app (login + every module). */}
          <Toastify />
          <Suspense fallback={<AppLoadingFallback />}>
            <Routes>
            {/* Default route - Login */}
            <Route path="/" element={<Login />}></Route>
            <Route path="/login" element={<Login />}></Route>
            <Route path="/register" element={<SignUp />}></Route>
            <Route path="/forgot-password" element={<ForgotPassword />}></Route>
            <Route
              path="/otpverification"
              element={<OTPVerification />}
            ></Route>
            <Route path="/ResetPassword" element={<ResetPassword />}></Route>
            <Route path="/addbank" element={<AddBankDetails />}></Route>
            <Route path="/sign-up-vendor" element={<Signupvendor />}></Route>
            <Route path="/finish-signup" element={<Finishsignup />}></Route>
            <Route path="/profile-under-review" element={<ProfileUnderReview />} />
            <Route element={<Layout />}>
              <Route path="dashboard" element={<Dashboard />} />
              <Route
                path="service-management"
                element={<ServiceManagement />}
              />
              <Route
                path="service-management/add-service"
                element={<AddService />}
              />
              <Route
                path="service-management/view-service"
                element={<ViewService />}
              />
              <Route
                path="service-management/rejected-services"
                element={<RejectedServices />}
              />
              <Route
                path="service-management/media-management"
                element={<MediaManagement />}
              />
              <Route
                path="booking-management"
                element={<Navigate to="/booking-management/other-services" replace />}
              />
              <Route
                path="booking-management/other-services"
                element={<BookingManagement />}
              />
              <Route
                path="booking-management/catering-booking"
                element={<CateringBookingManagement />}
              />
              <Route
                path="booking-management/view-details"
                element={<BookingDetails />}
              />
              <Route path="support-and-help" element={<SupportTickets />} />
              <Route
                path="support-and-help/ticket/:ticketId"
                element={<SupportTicketDetail />}
              />
              <Route path="disputes" element={<VendorDisputeList />} />
              <Route path="disputes/:disputeId" element={<VendorDisputeDetail />} />
              <Route path="package-management" element={<PackageManagementLayout />}>
                <Route index element={<Navigate to="inquiries" replace />} />
                <Route path="inquiries" element={<PackageInquiryList />} />
                <Route path="bookings" element={<PackageBookingList />} />
              </Route>
              <Route
                path="package-management/inquiries/:id"
                element={<PackageInquiryDetail />}
              />
              <Route
                path="package-management/bookings/:id"
                element={<PackageBookingDetail />}
              />
              <Route
                path="package-management/add-package"
                element={<Navigate to="/package-management/inquiries" replace />}
              />
              <Route
                path="package-management/package-details"
                element={<Navigate to="/package-management/inquiries" replace />}
              />
              <Route path="inventory" element={<Inventory />} />
              <Route path="product-management">
                <Route index element={<ProductManagementIndexRedirect />} />
                <Route path="offers" element={<Offers />} />
                <Route path="shop" element={<ProductManagement />} />
                <Route path="shop/add-product" element={<AddProductManagement />} />
                <Route path="shop/view-product" element={<ViewProductManagement />} />
                <Route path="rent" element={<ProductManagement />} />
                <Route path="rent/add-product" element={<AddProductManagement />} />
                <Route path="rent/view-product" element={<ViewProductManagement />} />
              </Route>
              <Route path="merchant-management">
                <Route index element={<MerchantManagementIndexRedirect />} />
                <Route path="shop" element={<MerchantManagement />} />
                <Route path="shop/view-order" element={<ViewOrder />} />
                <Route path="shop/view-order-product" element={<ViewProductManagement />} />
                <Route path="rent" element={<MerchantManagement />} />
                <Route path="rent/view-order" element={<ViewOrder />} />
                <Route path="rent/view-order-product" element={<ViewProductManagement />} />
              </Route>
              {/* <Route path="merchant-management"
                element={<Navigate to="/product-management/shop" replace />}
              />
              <Route
                path="merchant-management/add-product"
                element={<Navigate to="/product-management/shop/add-product" replace />}
              /> */}
              <Route path="document-management">
                <Route index element={<UserManagement />} />
              </Route>
              <Route path="My-Profile">
                <Route index element={<MyProfile />} />
              </Route>
              <Route path="Setting">
                <Route index element={<Setting />} />
              </Route>
              <Route path="notifications" element={<NotificationPage />} />
              <Route path="Terms-&-Condition" element={<CMSManagement />} />
              <Route path="Privacy-Policy" element={<Privacy />} />
              <Route path="FAQ" element={<FAQ />} />
            </Route>
            {/* Catch-all route - redirect to Login */}
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </Router>
    </DataProvider>
  );
}

export default App;
